import { refQR, refSCOR } from './swiss-refs';

/*
 * Les balises du camt.053 se cherchent par leur nom local, sans tenir compte
 * de la casse ni du préfixe d'espace de noms : « ns2:Ntry » est une « Ntry ».
 * Les noms s'écrivent ici en minuscules, les chemins séparés par des points.
 */

/** Le nom local d'un élément, en minuscules (« ns2:Ntry » → « ntry »). */
const nomLocal = (el) =>
  String(el.localName || el.nodeName || '')
    .replace(/^.*:/, '')
    .toLowerCase();

/** Les enfants directs d'un élément qui portent ce nom. */
const enfants = (el, nom) =>
  el
    ? [].slice.call(el.childNodes || []).filter((c) => c.nodeType === 1 && nomLocal(c) === nom)
    : [];

/** Les éléments au bout d'un chemin d'enfants directs (« rltdpties.cdtr.nm »). */
function chemin(el, ch) {
  let niveau = el ? [el] : [];
  for (const nom of ch.split('.')) niveau = niveau.flatMap((e) => enfants(e, nom));
  return niveau;
}

/** Le texte du premier élément au bout du chemin, ou ''. */
const texte = (el, ch) => {
  const e = chemin(el, ch)[0];
  return e ? String(e.textContent || '').trim() : '';
};

/** Tous les descendants qui portent ce nom, à n'importe quelle profondeur. */
function descendants(el, nom) {
  const out = [];
  const visite = (e) => {
    for (const c of [].slice.call(e.childNodes || [])) {
      if (c.nodeType !== 1) continue;
      if (nomLocal(c) === nom) out.push(c);
      visite(c);
    }
  };
  if (el) visite(el);
  return out;
}

/** Un montant écrit « 1250.00 », ou null s'il n'y en a pas. */
const montantDe = (el) => {
  if (!el) return null;
  const n = parseFloat(String(el.textContent || '').trim());
  return Number.isFinite(n) ? n : null;
};

/** Le nom d'une partie, en camt.053.001.04 (Dbtr/Nm) ou .08 (Dbtr/Pty/Nm). */
const nomDe = (el, partie) =>
  texte(el, ['rltdpties', partie, 'nm'].join('.')) ||
  texte(el, ['rltdpties', partie, 'pty', 'nm'].join('.'));

/** Le statut d'une écriture : « BOOK », « PDNG »… (texte, ou <Cd> en .08). */
const statutDe = (ntry) => {
  const sts = chemin(ntry, 'sts')[0];
  if (!sts) return '';
  return (texte(sts, 'cd') || String(sts.textContent || '')).trim().toUpperCase();
};

/** Les soldes d'ouverture et de clôture d'un relevé (Bal OPBD / CLBD), signés. */
function soldesDu(stmt) {
  const soldes = {};
  for (const bal of chemin(stmt, 'bal')) {
    const code = (texte(bal, 'tp.cdorprtry.cd') || texte(bal, 'tp.cdorprtry.prtry')).toUpperCase();
    const m = montantDe(chemin(bal, 'amt')[0]);
    if (m === null) continue;
    const signe = texte(bal, 'cdtdbtind') === 'DBIT' ? -1 : 1;
    if ((code === 'OPBD' || code === 'PRCD') && soldes.soldeInitial === undefined)
      soldes.soldeInitial = signe * m;
    if (code === 'CLBD') soldes.soldeFinal = signe * m;
  }
  return soldes;
}

/** Le montant en devise d'une transaction payée en francs (« EUR 45.00 »). */
function enDevise(tx) {
  for (const amt of [...chemin(tx, 'amtdtls.instdamt.amt'), ...chemin(tx, 'amtdtls.txamt.amt')]) {
    const attr = [].slice.call(amt.attributes || []).find((a) => a.name.toLowerCase() === 'ccy');
    const ccy = String(attr?.value || '').toUpperCase();
    const m = montantDe(amt);
    if (ccy && ccy !== 'CHF' && m !== null) return ccy + ' ' + m.toFixed(2);
  }
  return '';
}

/** La référence payée : celle du créancier (QR, SCOR), sinon une autre. */
function referenceDe(el) {
  const duCreancier = descendants(el, 'cdtrrefinf').flatMap((c) => enfants(c, 'ref'));
  for (const r of [...duCreancier, ...descendants(el, 'ref')]) {
    const t = String(r.textContent || '').replace(/\s/g, '');
    if (t) return t;
  }
  return '';
}

/** Les morceaux d'un libellé mis bout à bout, sans répéter ce qui y est déjà. */
function libelle(morceaux) {
  const out = [];
  for (const m of morceaux) {
    const t = String(m || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!t || out.join(' ').toLowerCase().includes(t.toLowerCase())) continue;
    out.push(t);
  }
  return out.join(' ');
}

/** Le sens d'une transaction : le sien, sinon celui de l'écriture. */
const sensDe = (tx, sensEcriture) => {
  const s = texte(tx, 'cdtdbtind');
  return s === 'DBIT' || s === 'CRDT' ? s : sensEcriture;
};

/** Les transactions d'une écriture (TxDtls, dans un lot ou non). */
const transactionsDe = (nt) =>
  chemin(nt, 'ntrydtls').flatMap((d) => [
    ...enfants(d, 'txdtls'),
    ...enfants(d, 'btch').flatMap((b) => enfants(b, 'txdtls')),
  ]);

/** Le montant d'une transaction (Amt, ou AmtDtls / TxAmt), ou null. */
const montantTx = (tx) =>
  montantDe(chemin(tx, 'amt')[0]) ?? montantDe(chemin(tx, 'amtdtls.txamt.amt')[0]);

/** Les lignes que donne une écriture (Ntry) du relevé. */
function lignesDe(nt) {
  const sensEcriture = texte(nt, 'cdtdbtind') === 'DBIT' ? 'DBIT' : 'CRDT';
  const montantEcriture = montantDe(chemin(nt, 'amt')[0]) || 0;
  let date = '';
  for (const ch of ['bookgdt.dt', 'bookgdt.dttm', 'valdt.dt', 'valdt.dttm']) {
    date = texte(nt, ch).slice(0, 10);
    if (date) break;
  }
  const info = texte(nt, 'addtlntryinf');
  const refEcriture = texte(nt, 'acctsvcrref') || texte(nt, 'ntryref');
  const txs = transactionsDe(nt);
  const montants = txs.map(montantTx);
  const signe = (tx, m) => (sensDe(tx, sensEcriture) === 'CRDT' ? 1 : -1) * m;
  const somme = txs.reduce((s, tx, i) => s + signe(tx, montants[i] || 0), 0);
  // Une ligne par transaction quand chacune dit son montant et que leur somme
  // fait l'écriture ; sinon l'écriture en une seule ligne.
  const separees =
    txs.length > 1 &&
    montants.every((m) => m !== null) &&
    Math.abs(Math.abs(somme) - montantEcriture) < 0.005;
  const ligne = (tx, i, montant, sens) => {
    const contrepartie =
      sens === 'DBIT'
        ? [nomDe(tx, 'cdtr'), nomDe(tx, 'ultmtcdtr')]
        : [nomDe(tx, 'dbtr'), nomDe(tx, 'ultmtdbtr')];
    const t = {
      date,
      montant,
      sens,
      label: libelle([
        info,
        ...contrepartie,
        ...chemin(tx, 'rmtinf.ustrd').map((u) => u.textContent),
        texte(tx, 'addtltxinf'),
        enDevise(tx),
      ]),
      ref: referenceDe(tx),
    };
    const refBanque =
      texte(tx, 'refs.acctsvcrref') ||
      (refEcriture ? refEcriture + (separees ? '#' + (i + 1) : '') : '');
    if (refBanque) t.refBanque = refBanque;
    return t;
  };
  if (separees) return txs.map((tx, i) => ligne(tx, i, montants[i], sensDe(tx, sensEcriture)));
  if (txs.length === 1) {
    const l = ligne(txs[0], 0, montantEcriture, sensEcriture);
    if (!l.ref) l.ref = referenceDe(nt);
    return [l];
  }
  // Sans détail exploitable : l'écriture, avec ce que ses transactions disent.
  const t = {
    date,
    montant: montantEcriture,
    sens: sensEcriture,
    label: libelle([
      info,
      ...txs.flatMap((tx) => [
        nomDe(tx, sensEcriture === 'DBIT' ? 'cdtr' : 'dbtr'),
        ...chemin(tx, 'rmtinf.ustrd').map((u) => u.textContent),
      ]),
    ]),
    ref: referenceDe(nt),
  };
  if (refEcriture) t.refBanque = refEcriture;
  return [t];
}

/**
 * Lit un relevé camt.053 (ou camt.052) : ses mouvements et ses soldes.
 *
 * <p>Une écriture qui regroupe plusieurs transactions (un lot de versements
 * QR : Btch, plusieurs TxDtls) donne une ligne par transaction, avec son
 * montant, sa contrepartie, sa référence et sa communication — en une seule
 * ligne, le premier versement prenait tout le lot. La contrepartie suit le
 * sens : pour une sortie, celui qu'on paie (Cdtr), pour une entrée, celui qui
 * paie (Dbtr) ; le premier nom venu était souvent le patron lui-même. Le
 * libellé réunit l'information de l'écriture, ce nom, toutes les
 * communications et l'information de la transaction. La référence de la
 * banque (AcctSvcrRef) distingue deux achats identiques du même jour. Une
 * écriture en attente (PDNG) n'est pas encore passée : elle ne se lit pas.
 *
 * @param {string} xmlText le fichier XML
 * @returns {{transactions: Array<{date: string, montant: number, sens: 'CRDT'|'DBIT',
 *   label: string, ref: string, refBanque?: string}>, soldeInitial?: number,
 *   soldeFinal?: number}|null} le relevé, ou null s'il n'est pas lisible
 */
export function lisCamt053(xmlText) {
  try {
    const doc = new DOMParser().parseFromString(String(xmlText || ''), 'text/xml');
    if (descendants(doc, 'parsererror').length) return null;
    const ntries = descendants(doc, 'ntry');
    if (!ntries.length) return null;
    const transactions = ntries.filter((nt) => statutDe(nt) !== 'PDNG').flatMap(lignesDe);
    const releves = [...descendants(doc, 'stmt'), ...descendants(doc, 'rpt')];
    // Les soldes ne se contrôlent que pour le relevé d'un seul compte.
    return { transactions, ...(releves.length === 1 ? soldesDu(releves[0]) : {}) };
  } catch (e) {
    return null;
  }
}

/**
 * Les mouvements d'un relevé camt.053 ({date, montant, sens, label, ref}).
 *
 * @param {string} xmlText le fichier XML
 * @returns {object[]|null} les mouvements, ou null s'il n'est pas lisible
 */
export function parseCamt053(xmlText) {
  const lu = lisCamt053(xmlText);
  return lu ? lu.transactions : null;
}

export function parseCamt054(xmlText) {
  try {
    if (typeof DOMParser === 'undefined') return [];
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const gt = (el, tag) => {
      const n = el.getElementsByTagName(tag);
      return n && n[0] ? (n[0].textContent || '').trim() : '';
    };
    const out = [];
    const ntrys = Array.from(doc.getElementsByTagName('Ntry'));
    for (const e of ntrys) {
      const sens = gt(e, 'CdtDbtInd');
      const eAmt = parseFloat(gt(e, 'Amt') || '0');
      // Le jour où l'argent est arrivé : la facture était datée payée du jour
      // de l'import.
      const jour = (() => {
        for (const tag of ['BookgDt', 'ValDt']) {
          const n = e.getElementsByTagName(tag)[0];
          const d = n ? (n.textContent || '').trim().slice(0, 10) : '';
          if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
        }
        return '';
      })();
      const txs = Array.from(e.getElementsByTagName('TxDtls'));
      if (txs.length) {
        for (const tx of txs) {
          let ref = '';
          const cri = tx.getElementsByTagName('CdtrRefInf');
          if (cri && cri[0]) {
            const rr = cri[0].getElementsByTagName('Ref');
            if (rr && rr[0]) ref = rr[0].textContent || '';
          }
          if (!ref) {
            const r2 = tx.getElementsByTagName('Ref');
            if (r2 && r2[0]) ref = r2[0].textContent || '';
          }
          const txAmt = parseFloat(gt(tx, 'Amt') || '') || eAmt;
          out.push({
            ref: (ref || '').replace(/\s/g, ''),
            amount: txAmt,
            credit: sens !== 'DBIT',
            date: jour,
          });
        }
      } else {
        out.push({ ref: '', amount: eAmt, credit: sens !== 'DBIT', date: jour });
      }
    }
    return out;
  } catch (e) {
    return [];
  }
}

export function rapprocheCamt(entries, factures, calcTtc, paiements) {
  const res = [];
  // Les versements déjà inscrits, facture par facture ({date: jj.mm.aaaa,
  // montant}) : le même relevé importé deux fois payait une seconde fois une
  // facture payée en partie (1'000.– devenus 2'000.–).
  const inscrits = new Map();
  (paiements instanceof Map ? [...paiements.entries()] : []).forEach(([id, l]) =>
    inscrits.set(id, [...(l || [])]),
  );
  const jourSuisse = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '');
  const dejaInscrit = (id, montant, iso) => {
    const l = inscrits.get(id) || [];
    const i = l.findIndex(
      (p) => Math.abs((+p.montant || 0) - montant) < 0.005 && (!iso || p.date === jourSuisse(iso)),
    );
    if (i < 0) return false;
    l.splice(i, 1);
    return true;
  };
  // Ce qui est payé, facture par facture, au fil du fichier : deux versements
  // pour la même facture s'additionnent (le second écrasait le premier).
  const payeDeja = new Map();
  for (const en of entries) {
    if (!en.credit) continue;
    const clean = (en.ref || '').replace(/\s/g, '').toUpperCase();
    if (!clean) continue;
    const rd = clean.replace(/\D/g, '');
    const f = factures.find((x) => {
      // La référence se dérive du numéro de facture, comme sur la pièce que le
      // client a reçue. La dériver de l'identifiant, comme ici auparavant, ne
      // rapprochait jamais rien : le client paie avec ce qui est imprimé.
      // Exception : la facture d'acompte d'une offre porte la référence du QR
      // de l'offre, que le serveur donne (identifiantReference) — c'est avec
      // ce QR que le client a versé l'acompte.
      const base = x.identifiantReference || x.num;
      const qd = refQR(base).replace(/\D/g, '');
      const sc = refSCOR(base).toUpperCase();
      return (rd && rd === qd) || clean === sc;
    });
    if (!f) continue;
    // Déjà payée avant l'import (le même relevé importé deux fois, ou le
    // paiement déjà noté) : rien à refaire. Elle était remarquée payée, et le
    // serveur refusait de modifier une facture payée.
    if (f.statut === 'Payée' || dejaInscrit(f.id, en.amount, en.date)) {
      res.push({ id: f.id, clientId: f.clientId, montant: en.amount, deja: true, statut: 'Payée' });
      continue;
    }
    const ttc = calcTtc(f);
    const avant = payeDeja.has(f.id) ? payeDeja.get(f.id) : +f.paidAmount || 0;
    const paid = Math.round((avant + en.amount) * 100) / 100;
    payeDeja.set(f.id, paid);
    const statut = paid >= ttc - 0.05 ? 'Payée' : 'Partielle';
    res.push({
      id: f.id,
      clientId: f.clientId,
      montant: en.amount,
      date: en.date || '',
      statut,
      acompte: f.type === 'acompte',
      paidTotal: paid,
    });
  }
  return res;
}
